import type {
  ITopFiveApiV2EndpointsStaffSharedModelsStaffItemDto,
} from '@/api/models';
import {
  useITopFiveApiV2EndpointsStaffGetStaffListGetStaffListEndpoint,
  getITopFiveApiV2EndpointsStaffGetStaffListGetStaffListEndpointQueryKey,
} from '@/api/api.generated';

/**
 * Query key for staff list endpoint
 */
export const staffListQueryKey =
  getITopFiveApiV2EndpointsStaffGetStaffListGetStaffListEndpointQueryKey();

/**
 * Response type for useStaffList hook
 */
export interface UseStaffListResult {
  /** List of staff items from API */
  staffList: ITopFiveApiV2EndpointsStaffSharedModelsStaffItemDto[];
  /** Whether user has iNominate admin access */
  hasAdminAccess: boolean;
  /** Whether user has iTopFive staff editor access */
  hasEditorAccess: boolean;
  /** Whether the query is loading */
  isLoading: boolean;
  /** Whether there was an error */
  isError: boolean;
  /** Error object if any */
  error: Error | null;
  /** Function to refetch the data */
  refetch: () => void;
}

/**
 * Custom hook to fetch staff list from the API
 *
 * Uses React Query for caching and state management.
 * Maps the API response to UI-friendly format.
 *
 * @example
 * ```tsx
 * const { staffList, isLoading, hasAdminAccess } = useStaffList();
 *
 * if (isLoading) return <Spinner />;
 *
 * return staffList.map(staff => <StaffCard key={staff.id} staff={staff} />);
 * ```
 */
export const useStaffList = (): UseStaffListResult => {
  const { data, isLoading, isError, error, refetch } =
    useITopFiveApiV2EndpointsStaffGetStaffListGetStaffListEndpoint({
      query: {},
      axios: { withCredentials: true },
    });

  return {
    staffList: data?.data?.staffList ?? [],
    hasAdminAccess: data?.data?.hasiNominateAccess ?? false,
    hasEditorAccess: data?.data?.hasiTopFiveStaffEditorAccess ?? false,
    isLoading,
    isError,
    error: error as Error | null,
    refetch,
  };
};

export default useStaffList;